import styles from './root.module.scss';
import AppHeader from './components/header/AppHeader';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppSelector } from '@customer/store/hooks';
import SideNav from './components/side-nav/SideNav';
import useSidePanel from '@customer/side-panels/useSidePanel';
import { Permission, userHasPermission } from '@customer/permissions';
import Footer from '@cas/components/footer/Footer';
import { SkeletonTheme } from 'react-loading-skeleton';
import { SuccessNotification, SuccessNotificationContext, useSuccessNotification } from '@dmc/ui';
import { VdsDrawer } from '@dmc/ui/vds';
import { useTranslation } from 'react-i18next';

const THEME_EXCLUDE_ROUTES = ['/'];
const HEADER_EXCLUDE_ROUTES = ['/'];
const NAV_EXCLUDE_ROUTES = ['/', '/no-access'];
const FOOTER_INCLUDE_ROUTES = ['/', '/no-access'];

const SkeletonBaseDark = '#0E0E0E'; // surface-high (dark mode)
const SkeletonBaseLight = '#FFFFFF'; // surface-high (light mode)

const SkeletonHighlightDark = '#162431'; // surface-lower (dark mode)
const SkeletonHighlightLight = '#F2F3F5'; // surface-lower (light mode)

export default function Root() {
  const { t } = useTranslation();
  const prefs = useAppSelector(state => state.prefs);
  const userData = useAppSelector(state => state.auth);

  const { message, countKey, showSuccessMessage, callback, callbackMessage, overrideTitle } =
    useSuccessNotification();

  const { isDrawerOpen, onDrawerClosed, activePanel } = useSidePanel();

  const location = useLocation();

  const vdsThemeClass =
    !THEME_EXCLUDE_ROUTES.includes(location.pathname) && prefs.darkTheme
      ? 'theme-dark'
      : 'theme-light';

  return (
    <VdsDrawer
      className={`${styles.drawer} ${vdsThemeClass}`}
      // Note: The data-theme attribute can be targeted within CSS modules for nested, scoped styling using @theme-light/@theme-dark.
      data-theme={prefs.darkTheme ? 'dark' : 'light'}
      opened={isDrawerOpen}
      showCloseButton={true}
      backdrop={true}
      mode="over"
      position="right"
      padding="spacing-07"
      isAdaptable={true}
      onDrawerClosed={onDrawerClosed}
    >
      <SkeletonTheme
        baseColor={prefs.darkTheme ? SkeletonBaseDark : SkeletonBaseLight}
        highlightColor={prefs.darkTheme ? SkeletonHighlightDark : SkeletonHighlightLight}
      >
        <SuccessNotificationContext.Provider value={{ showSuccessMessage }}>
          <div slot="container" className={styles.dm_app}>
            {!NAV_EXCLUDE_ROUTES.includes(location.pathname) &&
              userHasPermission(userData, Permission.CanAccess) && <SideNav />}

            {/* Main content */}
            <div className={styles.dm_body}>
              {!HEADER_EXCLUDE_ROUTES.includes(location.pathname) && <AppHeader />}

              <main className={styles.dm_body__outlet}>
                <Outlet />
              </main>

              {FOOTER_INCLUDE_ROUTES.includes(location.pathname) && <Footer />}
            </div>

            <SuccessNotification
              countKey={countKey}
              message={message}
              onClickCallback={callback}
              callbackMessage={callbackMessage}
              title={overrideTitle || t('Success')}
            />
          </div>

          {/* Right side panel */}
          <aside slot="drawer" className={styles.parent_container}>
            <div className={styles.child_container}>{activePanel}</div>
          </aside>
        </SuccessNotificationContext.Provider>
      </SkeletonTheme>
    </VdsDrawer>
  );
}
